import type { ApiClientConfig, TokenProvider } from '@klareospace/pharos-lib-types';
import {
  composeApiLoggers,
  createApiLogger,
  createTelemetryApiLogger,
  createTokenProviderRef,
} from '@klareospace/pharos-lib-front-utils';

import { env } from '&/config';
import { telemetryClient } from './telemetry';

/** API client configurations for the frontoffice app */
export const apiConfigs: Record<string, ApiClientConfig> = {
  'admin-auth': {
    baseURL: `${env.apiBaseUrl}/admin-auth`,
    timeout: 10_000,
  },
  bill: {
    baseURL: `${env.apiBaseUrl}/bill`,
    timeout: 10_000,
  },
  deliveries: {
    baseURL: `${env.apiBaseUrl}/deliveries`,
    timeout: 10_000,
  },
  geo: {
    baseURL: `${env.apiBaseUrl}/geo`,
    timeout: 10_000,
  },
  messenger: {
    baseURL: `${env.apiBaseUrl}/messenger`,
    timeout: 10_000,
  },
};

/**
 * Composed logger: console (dev-only debug + always error) PLUS telemetry.
 * Console behaviour is unchanged — the telemetry logger is added additively.
 */
export const apiLogger = composeApiLoggers(createApiLogger(env.isDev), createTelemetryApiLogger(telemetryClient));

/** Correlation ID prefix for frontoffice */
export const CORRELATION_ID_PREFIX = 'fo';

/**
 * Token provider ref — backed by a const ref object rather than a `let` variable.
 * This indirection is needed because ApiClient instances are memoized — the stable
 * tokenProvider reference delegates to the latest getToken from auth context.
 */
const { setTokenProvider, tokenProvider: userTokenProvider } = createTokenProviderRef();
export { setTokenProvider };

/**
 * Signup-session token (PHA-609) — held in memory only, never persisted (localStorage/
 * sessionStorage), and mutually exclusive with the authenticated user's token. Backed by a ref
 * object rather than a `let` variable, matching `createTokenProviderRef`'s own convention.
 */
const signupSessionTokenRef: { current: string | null } = { current: null };

export function setSignupSessionToken(token: string | null): void {
  signupSessionTokenRef.current = token;
}

/** Resolves the authenticated user's token first, falling back to an in-flight signup-session token. */
export const tokenProvider: TokenProvider = async (): Promise<string | null> => {
  const userToken = await userTokenProvider();
  return userToken ?? signupSessionTokenRef.current;
};
